import { useCallback } from 'react';
import type { Location } from 'history';
import { useLocationSelector } from '@shared/core';
import { getSlugFromPathname } from '@apps/guest/packages/layout-engine/common/utils/getPageInfo';
import type { PagesRefs } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.types';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { EventPageType } from '@graphql/generated-base';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';

type PathSlug = ReturnType<typeof getSlugFromPathname>;

const selectPathSlug = (location: Pick<Location, 'pathname'>): PathSlug => getSlugFromPathname(location.pathname);
const isSamePathSlug = (a: PathSlug, b: PathSlug) => a.pagePath === b.pagePath && a.isCustomPagePath === b.isCustomPagePath;

export const usePageRefFromUrl = (pages?: ReadonlyArray<EventPageFragment>) => {
  // Selected, not read off `history.location` with the location in the dependency list: that made a
  // fresh callback on every URL change, and the effects keyed on it re-ran for URLs that resolve to
  // the same page. Callers now re-render only when the slug in the path actually changes.
  const { pagePath: slugFromPathname, isCustomPagePath } = useLocationSelector(selectPathSlug, isSamePathSlug);
  const { defaultPage } = useDefaultPage(pages);

  const getPageRefFromUrl = useCallback(
    (pagesRef: ReadonlyArray<PagesRefs>) => {
      // If no slug in URL (root route), return the default page ref
      if (!slugFromPathname && defaultPage) {
        return pagesRef.find(pageRef => pageRef.page.pageSlug === defaultPage.pageSlug);
      }

      const currentPage = pagesRef.find(
        pageRef => slugFromPathname && pageRef.page.pageSlug.toLowerCase() === slugFromPathname?.toLowerCase() && (!isCustomPagePath || pageRef.page.type === EventPageType.custom),
      );
      return currentPage;
    },
    [slugFromPathname, isCustomPagePath, defaultPage],
  );

  return getPageRefFromUrl;
};
